import type { InquiryLabel } from '@/services/inquiryLabels';

const normalizeKey = (key: string): string => key.toLowerCase().trim();

/**
 * Formats a label for display in the UI.
 *
 * Display rules:
 * - Tag mode (value === true): Show only the key, e.g., "urgent"
 * - Key-value mode (string value): Show "key: value", e.g., "age: 65"
 * - Redundant pairs (key === value): Show only the key, e.g., "new" instead of "new: new"
 * - Empty value: Show only the key
 */
export const formatLabelText = (label: InquiryLabel): string => {
  const normalizedKey = normalizeKey(label.key);

  const value = label.value;

  if (!normalizedKey) {
    return typeof value === 'string' ? value.trim() : '';
  }

  if (value === true || typeof value !== 'string') {
    return label.key.trim();
  }

  const trimmedValue = value.trim();

  if (!trimmedValue || normalizedKey === normalizeKey(trimmedValue)) {
    return label.key.trim();
  }

  return `${label.key.trim()}: ${trimmedValue}`;
};

/**
 * Sorts labels alphabetically by key.
 */
export const sortLabels = (labels: InquiryLabel[]): InquiryLabel[] =>
  [...labels].sort((a, b) => normalizeKey(a.key).localeCompare(normalizeKey(b.key)));
